import React, { useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';

const C = {
    blue:      '#1976d2',
    blueLight: '#e3f0fc',
    blueDark:  '#1256a0',
    border:    '#e0e4ea',
    text:      '#1a1d23',
    muted:     '#6b7280',
    radius:    '6px',
};

// ─── Universal response helpers ───────────────────────────────────────────────

/**
 * Extract a single part object from any response shape.
 * Handles: { found, result } | { found, sources[] } | { product/item/part }
 *          | { results/items/parts/sources/hits/products[] } | direct array | flat part
 */
const extractPart = (data) => {
    if (!data) return null;

    // { found: bool, result/sources/... }
    if ('found' in data) {
        if (!data.found) return null;
        // single result object
        if (data.result && typeof data.result === 'object' && !Array.isArray(data.result))
            return data.result;
    }

    // Direct array
    if (Array.isArray(data)) return data[0] || null;

    // Wrapped array — covers all known envelope keys including 'sources'
    for (const key of ['sources', 'results', 'items', 'parts', 'hits', 'products', 'matches', 'components', 'data']) {
        const val = data[key];
        if (Array.isArray(val) && val.length > 0) return val[0];
    }

    // Wrapped single object
    for (const key of ['product', 'item', 'part', 'result', 'data']) {
        const val = data[key];
        if (val && typeof val === 'object' && !Array.isArray(val)) return val;
    }

    // Response itself looks like a part
    if (data.mpn || data.mfr || data.part_number || data.partNumber ||
        data.ManufacturerPartNumber || data.manufacturer_part_number) return data;

    return null;
};

/**
 * Safely read a value from obj by trying multiple dot-path keys.
 * Returns the first non-null/undefined/empty value, or null.
 */
const pick = (obj, ...keys) => {
    for (const key of keys) {
        const val = key.split('.').reduce((o, k) => (o != null ? o[k] : undefined), obj);
        if (val !== undefined && val !== null && val !== '') return val;
    }
    return null;
};

/** Parse `extra` field — handles object, JSON string, or double-encoded string. */
const parseExtra = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    try {
        let p = JSON.parse(raw);
        if (typeof p === 'string') p = JSON.parse(p);
        return p && typeof p === 'object' ? p : {};
    } catch { return {}; }
};

/**
 * Normalize price breaks from any format → [{ break_quantity, unit_price }]
 * Sources: LCSC { qFrom, price } | DigiKey { BreakQuantity, UnitPrice }
 *          | generic { min_qty/quantity, price/unit_price }
 */
const normalizePriceBreaks = (raw) => {
    if (!raw) return [];
    let arr = raw;
    if (typeof raw === 'string') { try { arr = JSON.parse(raw); } catch { return []; } }
    if (!Array.isArray(arr)) return [];
    return arr
        .map((pb) => ({
            break_quantity: pb.break_quantity ?? pb.BreakQuantity ?? pb.qFrom ?? pb.min_qty ?? pb.quantity ?? 1,
            unit_price:     pb.unit_price     ?? pb.UnitPrice     ?? pb.price ?? 0,
        }))
        .filter((pb) => pb.unit_price > 0);
};

/**
 * Normalize parameters/attributes → [{ parameter_name, value }]
 * Handles: { ParameterText, ValueText } | { parameter_name, value }
 *          | { name, value } | plain { key: value } object
 */
const normalizeParameters = (part, ex, raw) => {
    // Try every known array source, prefer part-level then raw_data
    for (const candidate of [
        part.parameters, part.Parameters,
        raw?.Parameters,
        ex.parameters,
    ]) {
        if (Array.isArray(candidate) && candidate.length > 0) {
            return candidate
                .map((p) => ({
                    parameter_name: p.parameter_name || p.ParameterText || p.name || p.key || '',
                    value: String(p.value ?? p.ValueText ?? p.value_text ?? p.UnitText ?? ''),
                }))
                .filter((p) => p.parameter_name && p.value);
        }
    }
    // Fallback: plain key→value object
    for (const candidate of [ex.attributes, part.attributes, ex.specs, part.specs]) {
        if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
            return Object.entries(candidate).map(([k, v]) => ({
                parameter_name: k,
                value: String(v),
            }));
        }
    }
    return [];
};

/**
 * Pick the best ProductVariation price breaks from DigiKey.
 * Prefers lowest MOQ variation (usually Cut Tape) for single-unit sellers.
 */
const digikeyPriceBreaks = (productVariations) => {
    if (!Array.isArray(productVariations) || !productVariations.length) return [];
    const best = [...productVariations].sort(
        (a, b) => (a.MinimumOrderQuantity ?? 999999) - (b.MinimumOrderQuantity ?? 999999)
    )[0];
    return normalizePriceBreaks(best?.StandardPricing);
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function MpnLookup({ onResult }) {
    const [query, setQuery]     = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState(null);

    const handleLookup = async () => {
        if (!query.trim()) return;
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_PARTS_SEARCH_URL}?q=${encodeURIComponent(query.trim())}`,
                { credentials: 'include' }
            );
            if (!res.ok) throw new Error(`Lookup failed (HTTP ${res.status})`);
            const data = await res.json();

            const part = extractPart(data);
            if (!part) {
                setError('No part found for this MPN. Fill in the form manually.');
                return;
            }

            // DigiKey responses embed extra detail in raw_data
            const raw = part.raw_data || {};
            const ex  = parseExtra(part.extra);
            const cls = raw.Classifications || {};

            // ── Price breaks ──────────────────────────────────────────────────
            // DigiKey: raw_data.ProductVariations[].StandardPricing (pick lowest MOQ)
            // LCSC / others: part.price / ex.price_breaks / ex.prices
            const priceBreaks =
                digikeyPriceBreaks(raw.ProductVariations) ||
                normalizePriceBreaks(
                    ex.price_breaks || ex.prices ||
                    part.price_breaks || part.prices || part.price ||
                    part.StandardPricing
                );

            // ── Parameters ───────────────────────────────────────────────────
            const parameters = normalizeParameters(part, ex, raw);

            // ── Photo ─────────────────────────────────────────────────────────
            const photoUrl =
                pick(raw,  'PhotoUrl') ||
                pick(part, 'image', 'photo_url', 'image_url', 'PhotoUrl', 'PrimaryPhoto') ||
                pick(ex,   'photo_url', 'image_url') ||
                (Array.isArray(ex.images)   ? ex.images[0]?.['224x224']   || ex.images[0]?.['96x96']   : null) ||
                (Array.isArray(part.images) ? part.images[0]?.['224x224'] || part.images[0]?.['96x96'] : null);

            // ── Datasheet — normalise protocol-relative URLs ──────────────────
            const datasheetRaw =
                pick(raw,  'DatasheetUrl') ||
                pick(part, 'datasheet_url', 'datasheet', 'Datasheet') ||
                pick(ex,   'datasheet.pdf', 'datasheet_url');
            const datasheetUrl = datasheetRaw?.startsWith('//')
                ? `https:${datasheetRaw}`
                : (datasheetRaw || '');

            // ── RoHS (bool or string) ─────────────────────────────────────────
            const rohsRaw =
                pick(cls,  'RohsStatus') ||
                pick(part, 'rohs_status', 'RoHSStatus') ||
                pick(ex,   'rohs_status', 'rohs');
            const rohsStatus = typeof rohsRaw === 'boolean'
                ? (rohsRaw ? 'RoHS Compliant' : null)
                : (rohsRaw || null);

            // ── Lifecycle ─────────────────────────────────────────────────────
            const discontinued = raw.Discontinued ?? part.discontinued ?? false;
            const eol          = raw.EndOfLife     ?? part.end_of_life  ?? false;
            const lifecycleStatus =
                pick(ex, 'lifecycle_status') ||
                pick(part, 'lifecycle_status', 'part_status') ||
                (discontinued ? 'discontinued' : eol ? 'obsolete' : 'active');

            // ── Other part numbers ────────────────────────────────────────────
            const otherNames =
                (Array.isArray(raw.OtherNames) ? raw.OtherNames : null) ||
                (Array.isArray(ex.other_part_numbers) ? ex.other_part_numbers : null) ||
                (Array.isArray(part.other_part_numbers) ? part.other_part_numbers : null);
            const otherPartNumbers = otherNames ? otherNames.join(', ') : '';

            // ── Series ────────────────────────────────────────────────────────
            const seriesRaw = pick(raw, 'Series.Name') || pick(raw, 'BaseProductNumber.Name') ||
                              pick(part, 'series', 'Series') || pick(ex, 'series');
            const series = (seriesRaw === '-' || seriesRaw === 'N/A') ? '' : (seriesRaw || '');

            // ── Category ─────────────────────────────────────────────────────
            // DigiKey nests: Category > ChildCategories[] > ChildCategories[] > Name (deepest = most specific)
            const deepestCategory = (node) => {
                if (!node) return null;
                const children = node.ChildCategories;
                if (Array.isArray(children) && children.length > 0) return deepestCategory(children[0]);
                return node.Name || null;
            };
            const category =
                deepestCategory(raw.Category) ||
                pick(ex, 'category.name2', 'category.name1', 'category') ||
                pick(part, 'category', 'Category', 'FamilyName') || '';
            const categoryPath =
                pick(raw, 'Category.Name') ||
                pick(ex, 'category.name1', 'category_path') ||
                pick(part, 'category_path') || '';

            // ── MOQ — prefer lowest from ProductVariations ────────────────────
            const moq = (Array.isArray(raw.ProductVariations) && raw.ProductVariations.length)
                ? Math.min(...raw.ProductVariations.map((v) => v.MinimumOrderQuantity ?? 999999))
                : (pick(ex, 'moq') ?? pick(part, 'minimum_order_quantity', 'MinimumOrderQuantity') ?? null);

            const fields = {
                mpn: pick(ex, 'mpn') || pick(raw, 'ManufacturerProductNumber') ||
                     pick(part, 'part_number', 'mpn', 'mfr', 'partNumber',
                          'ManufacturerPartNumber', 'manufacturer_part_number') || '',

                manufacturer:
                    pick(raw,  'Manufacturer.Name') ||
                    pick(part, 'manufacturer', 'Manufacturer', 'manufacturer_name') ||
                    pick(ex,   'manufacturer.name', 'manufacturer') || '',

                brand:
                    pick(ex, 'brand') || pick(part, 'brand', 'Brand') || '',

                series,
                other_part_numbers: otherPartNumbers,
                category,
                category_path:          categoryPath,

                description:
                    pick(raw,  'Description.ProductDescription') ||
                    pick(part, 'description', 'Description', 'ProductDescription') ||
                    pick(ex,   'description') || '',

                detailed_description:
                    pick(raw,  'Description.DetailedDescription') ||
                    pick(part, 'detailed_description', 'DetailedDescription') ||
                    pick(ex,   'detailed_description') || '',

                package:
                    pick(ex,   'package') ||
                    pick(part, 'package', 'Package', 'PackageType', 'Packaging') || '',

                mounting_type:
                    pick(part, 'mounting_type', 'MountingType') ||
                    pick(ex,   'mounting_type') || '',

                minimum_order_quantity: moq,

                lead_time_weeks:
                    pick(raw,  'ManufacturerLeadWeeks') != null
                        ? Number(pick(raw, 'ManufacturerLeadWeeks'))
                        : (pick(ex, 'lead_time_weeks') ?? pick(part, 'lead_time_weeks') ?? null),

                back_order_allowed: raw.BackOrderNotAllowed === false ? true
                    : (ex.back_order_allowed ?? part.back_order_allowed ?? null),

                ncnr:            raw.Ncnr           ?? ex.ncnr           ?? part.ncnr           ?? null,
                lifecycle_status: lifecycleStatus,
                end_of_life:     eol,

                datasheet_url:   datasheetUrl,
                photo_url:       photoUrl || '',
                rohs_status:     rohsStatus || '',

                reach_status:
                    pick(cls,  'ReachStatus') ||
                    pick(ex,   'reach_status') ||
                    pick(part, 'reach_status', 'ReachStatus') || '',

                moisture_sensitivity_level:
                    pick(cls,  'MoistureSensitivityLevel') ||
                    pick(ex,   'moisture_sensitivity_level') ||
                    pick(part, 'moisture_sensitivity_level') || '',

                export_control_class:
                    pick(cls,  'ExportControlClassNumber') ||
                    pick(ex,   'export_control_class') ||
                    pick(part, 'export_control_class') || '',

                htsus_code:
                    pick(cls,  'HtsusCode') ||
                    pick(ex,   'htsus_code') ||
                    pick(part, 'htsus_code', 'HtsusCode') || '',
            };

            onResult(fields, priceBreaks, parameters);
        } catch (err) {
            setError(err.message || 'Lookup failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            mb: 2.5, p: 2, bgcolor: C.blueLight,
            borderRadius: C.radius, border: `1px solid ${C.border}`,
        }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text, mb: 1 }}>
                Auto-fill from Part Number
            </Typography>
            <Stack direction="row" spacing={1}>
                <TextField
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                    placeholder="e.g. MMSZ5231B-7-F"
                    size="small"
                    sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: C.radius, fontSize: 13 } }}
                />
                <Button
                    variant="contained"
                    onClick={handleLookup}
                    disabled={loading || !query.trim()}
                    sx={{
                        bgcolor: C.blue, textTransform: 'none', fontWeight: 600,
                        fontSize: 13, borderRadius: C.radius, boxShadow: 'none',
                        whiteSpace: 'nowrap',
                        '&:hover': { bgcolor: C.blueDark, boxShadow: 'none' },
                    }}>
                    {loading ? 'Looking up…' : 'Lookup Part'}
                </Button>
            </Stack>
            {error && (
                <Alert severity="warning" sx={{ mt: 1, fontSize: 12, py: 0.5 }}>
                    {error}
                </Alert>
            )}
            <Typography sx={{ fontSize: 11, color: C.muted, mt: 0.8 }}>
                Fetches part data automatically — you only need to enter price and quantity.
            </Typography>
        </Box>
    );
}
